import type { SupportedLocale } from '@brightdrop/localization-core';
import { useCallback, useState } from 'react';

import { getActiveLocale } from '~/common/utils/locale.utils';
import {
  getStoredLocale,
  setStoredLocale,
} from '~/common/utils/signed-out-locale.utils';

/**
 * Holds the locale a visitor picked before they had a profile. The choice is
 * kept in local storage because switching between SSO and CDI reloads the
 * page, the return from account creation can land in another tab, business
 * enrollment reads it until the organization exists, and the login screen and
 * Quick Pay read the same value.
 *
 * With nothing valid stored, the locale is the one the app resolved from the
 * browser setting, which itself falls back to the regional default.
 * @returns The selected locale and a setter that also persists it
 */
const useStoredLocale = (): [
  SupportedLocale,
  (locale: SupportedLocale) => void,
] => {
  const [locale, setLocale] = useState<SupportedLocale>(
    () => getStoredLocale() ?? getActiveLocale()
  );

  const updateLocale = useCallback((next: SupportedLocale) => {
    setStoredLocale(next);
    setLocale(next);
  }, []);

  return [locale, updateLocale];
};

export default useStoredLocale;
